import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { getItemById } from '@/services/itemService';
import type { Item } from '@packages/shared-types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';

const PRINT_QR_BODY_CLASS = 'print-qr-active';
const FETCH_CHUNK_SIZE = 50;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

const QR_SIZE_PX: Record<'small' | 'medium' | 'big', number> = {
  small: 192,
  medium: 256,
  big: 320,
};

/** Default columns when not provided (derived from QR size). */
function getDefaultColumnsPerRow(qrSize: 'small' | 'medium' | 'big'): number {
  const qrWidth = QR_SIZE_PX[qrSize];
  const cardWidth = qrWidth + 48;
  const cols = Math.floor(720 / cardWidth);
  return Math.max(1, Math.min(4, cols));
}

type PaperSize = 'a4' | 'letter';
type Orientation = 'portrait' | 'landscape';
type ColumnsPerRow = 1 | 2 | 3 | 4;
type RowsPerPage = 2 | 3 | 4 | 5 | 6;

const CARD_PADDING_PX = 48;

/** 96dpi: 1mm ≈ 3.7795px. Printable area = page minus 10mm margin each side (injected @page). */
const MM_TO_PX = 96 / 25.4;

function getPrintableAreaPx(paper: PaperSize, orientation: Orientation): { width: number; height: number } {
  const { widthMm, heightMm } = getPrintableAreaMm(paper, orientation);
  return {
    width: widthMm * MM_TO_PX,
    height: heightMm * MM_TO_PX,
  };
}

/** Printable area in mm (page minus 10mm margin each side). Used for print CSS .print-page size. */
function getPrintableAreaMm(paper: PaperSize, orientation: Orientation): { widthMm: number; heightMm: number } {
  if (paper === 'letter') {
    const wMm = 8.5 * 25.4 - 20;
    const hMm = 11 * 25.4 - 20;
    return orientation === 'portrait' ? { widthMm: wMm, heightMm: hMm } : { widthMm: hMm, heightMm: wMm };
  }
  const widthMm = 210 - 20;
  const heightMm = 297 - 20;
  return orientation === 'portrait' ? { widthMm, heightMm } : { widthMm: heightMm, heightMm: widthMm };
}

type PrintType = 'qr' | 'barcode';

const BARCODE_SCALE = 2;

function generateBarcodeDataUrl(value: string, heightPx: number): string {
  const canvas = document.createElement('canvas');
  const h = heightPx * BARCODE_SCALE;
  const barWidth = 2.5 * BARCODE_SCALE;
  const margin = 6 * BARCODE_SCALE;
  const fontSize = 14 * BARCODE_SCALE;
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: barWidth,
    height: h,
    margin,
    displayValue: true,
    fontOptions: 'bold',
    fontSize,
  });
  const w = canvas.width;
  const height = canvas.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = height;
  const ctx = out.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, height);
    ctx.drawImage(canvas, 0, 0);
  }
  return out.toDataURL('image/png');
}

type PrintState = {
  items?: Item[];
  itemIds?: string[];
  qrSize?: 'small' | 'medium' | 'big';
  includeTitle?: boolean;
  includeSku?: boolean;
} | null;

const PRINT_PAGE_STYLE_ID = 'print-qr-page-style';

export function ItemPrintQrBatchPage(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location.state as PrintState) ?? {};
  const itemsFromState = navState.items ?? [];
  const itemIdsFromState = navState.itemIds ?? [];

  const [paperSize, setPaperSize] = useState<PaperSize>(() => 'a4');
  const [orientation, setOrientation] = useState<Orientation>(() => 'portrait');
  const [columnsPerRow, setColumnsPerRow] = useState<ColumnsPerRow>(() => {
    const size = navState.qrSize ?? 'medium';
    return getDefaultColumnsPerRow(size) as ColumnsPerRow;
  });
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(() => 4);
  const [qrSize, setQrSize] = useState<'small' | 'medium' | 'big'>(() => navState.qrSize ?? 'medium');
  const [includeTitle, setIncludeTitle] = useState(() => navState.includeTitle ?? true);
  const [includeSku, setIncludeSku] = useState(() => navState.includeSku ?? true);
  const [printType, setPrintType] = useState<PrintType>(() => 'qr');

  const [items, setItems] = useState<Item[]>([]);
  const [qrById, setQrById] = useState<Record<string, string>>({});
  const [barcodeById, setBarcodeById] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printContentRef = useRef<HTMLDivElement>(null);

  const qrWidth = QR_SIZE_PX[qrSize];
  const minCellWidthPx = qrWidth + CARD_PADDING_PX;
  const labelsPerPage = columnsPerRow * rowsPerPage;
  const pageChunks = useMemo(
    () => chunk(items, labelsPerPage),
    [items, labelsPerPage],
  );

  const printableArea = useMemo(
    () => getPrintableAreaPx(paperSize, orientation),
    [paperSize, orientation],
  );
  const previewScale = useMemo(() => {
    const maxSide = Math.max(printableArea.width, printableArea.height);
    return Math.min(1, 1200 / maxSide);
  }, [printableArea.width, printableArea.height]);
  const previewWidth = Math.round(printableArea.width * previewScale);
  const previewHeight = Math.round(printableArea.height * previewScale);

  const hasItemsFromState = itemsFromState.length > 0;
  const hasItemIdsOnly = !hasItemsFromState && itemIdsFromState.length > 0;

  useEffect(() => {
    document.body.classList.add(PRINT_QR_BODY_CLASS);
    return () => document.body.classList.remove(PRINT_QR_BODY_CLASS);
  }, []);

  useEffect(() => {
    const size = paperSize === 'letter' ? 'letter' : 'A4';
    const rule = `${size} ${orientation}`;
    const { widthMm, heightMm } = getPrintableAreaMm(paperSize, orientation);
    let el = document.getElementById(PRINT_PAGE_STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = PRINT_PAGE_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = `@media print {
  @page { size: ${rule}; margin: 10mm; }
  body.print-qr-active .print-qr-content {
    padding: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
  }
  body.print-qr-active .print-qr-content .print-page {
    width: ${widthMm}mm !important;
    height: ${heightMm}mm !important;
    min-width: ${widthMm}mm !important;
    min-height: ${heightMm}mm !important;
    max-width: ${widthMm}mm !important;
    max-height: ${heightMm}mm !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    page-break-after: always;
    margin: 0 !important;
    padding: 0 !important;
  }
  body.print-qr-active .print-qr-content .print-page:last-child { page-break-after: auto; }
  body.print-qr-active .print-qr-content .print-page .print-qr-grid {
    width: 100% !important;
    margin: 0 !important;
  }
}`;
    return () => {
      el?.remove();
    };
  }, [paperSize, orientation]);

  const barcodeHeightPx = Math.round(QR_SIZE_PX[qrSize] * 0.65);

  useEffect(() => {
    const s = (location.state as PrintState) ?? {};
    const itemsFromNav = s.items ?? [];
    const itemIdsFromNav = s.itemIds ?? [];
    const hasItems = itemsFromNav.length > 0;
    const hasIdsOnly = itemIdsFromNav.length > 0 && !hasItems;
    const width = QR_SIZE_PX[qrSize];

    const generateForItems = (list: Item[]): void => {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.trushelf.io';
      Promise.all(
        list.map(async (item) => {
          const qrUrl = await QRCode.toDataURL(`${origin}/items/${item.id}`, {
            width,
            margin: 2,
          });
          return [item.id, qrUrl] as const;
        }),
      )
        .then((entries) => setQrById(Object.fromEntries(entries)))
        .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));

      try {
        const barcodeEntries = list.map((item) => {
          const value = item.sku?.trim() || item.id;
          const dataUrl = generateBarcodeDataUrl(value, barcodeHeightPx);
          return [item.id, dataUrl] as const;
        });
        setBarcodeById(Object.fromEntries(barcodeEntries));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate barcodes');
      }
    };

    if (hasItems) {
      setItems(itemsFromNav);
      generateForItems(itemsFromNav);
      return;
    }

    if (!hasIdsOnly) return;

    const run = async (): Promise<void> => {
      const idChunks = chunk(itemIdsFromNav, FETCH_CHUNK_SIZE);
      const fetched: Item[] = [];
      for (const ids of idChunks) {
        const batch = await Promise.all(ids.map((id) => getItemById(id)));
        fetched.push(...batch);
      }
      setItems(fetched);
      generateForItems(fetched);
    };

    run().catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [location.state, qrSize, barcodeHeightPx]);

  const runPrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = printType === 'barcode' ? 'Barcodes' : 'QR Codes';
    const restoreTitle = (): void => {
      document.title = previousTitle;
      window.removeEventListener('afterprint', restoreTitle);
      setIsPrinting(false);
    };
    window.addEventListener('afterprint', restoreTitle);

    const container = printContentRef.current;
    const allImgs = container?.querySelectorAll<HTMLImageElement>('img') ?? [];
    if (!allImgs.length) {
      window.print();
      setTimeout(restoreTitle, 500);
      return;
    }
    const loadPromises = Array.from(allImgs).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            if (typeof img.decode === 'function') {
              img.decode().then(() => resolve()).catch(() => resolve());
            } else {
              resolve();
            }
            return;
          }
          img.onload = () => {
            if (typeof img.decode === 'function') {
              img.decode().then(() => resolve()).catch(() => resolve());
            } else {
              resolve();
            }
          };
          img.onerror = () => resolve();
        }),
    );
    Promise.all(loadPromises).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            requestAnimationFrame(() => {
              window.print();
              setTimeout(restoreTitle, 500);
            });
          }, 300);
        });
      });
    });
  }, [printType]);

  if (!hasItemsFromState && !hasItemIdsOnly) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
        <p className="text-muted-foreground">{t('pages.items.printQrBatch.noItems')}</p>
        <button
          type="button"
          className="text-primary underline"
          onClick={() => navigate('/items')}
        >
          {t('pages.items.printQrBatch.backToItems')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const ready =
    items.length > 0 &&
    (printType === 'barcode'
      ? items.every((i) => barcodeById[i.id])
      : items.every((i) => qrById[i.id]));

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <p className="text-muted-foreground">{t('pages.items.list.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="print-qr-no-print border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="mx-auto flex max-w-5xl flex-wrap items-end gap-4 gap-y-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => navigate('/items')}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t('pages.items.printQrBatch.backToItems')}
          </Button>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-700">
              {t('pages.items.printQrBatch.optionsTitle')}
            </span>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-paper" className="text-xs text-gray-600">
                {t('pages.items.printQrBatch.paperSize')}
              </Label>
              <Select
                value={paperSize}
                onValueChange={(v) => setPaperSize(v as PaperSize)}
              >
                <SelectTrigger id="print-paper" className="h-8 w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a4">{t('pages.items.printQrBatch.paperA4')}</SelectItem>
                  <SelectItem value="letter">{t('pages.items.printQrBatch.paperLetter')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-orientation" className="text-xs text-gray-600">
                {t('pages.items.printQrBatch.orientation')}
              </Label>
              <Select
                value={orientation}
                onValueChange={(v) => setOrientation(v as Orientation)}
              >
                <SelectTrigger id="print-orientation" className="h-8 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">{t('pages.items.printQrBatch.orientationPortrait')}</SelectItem>
                  <SelectItem value="landscape">{t('pages.items.printQrBatch.orientationLandscape')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-cols" className="text-xs text-gray-600">
                {t('pages.items.printQrBatch.columns')}
              </Label>
              <Select
                value={String(columnsPerRow)}
                onValueChange={(v) => setColumnsPerRow(Number(v) as ColumnsPerRow)}
              >
                <SelectTrigger id="print-cols" className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {([1, 2, 3, 4] as const).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-rows" className="text-xs text-gray-600">
                {t('pages.items.printQrBatch.rowsPerPage')}
              </Label>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(v) => setRowsPerPage(Number(v) as RowsPerPage)}
              >
                <SelectTrigger id="print-rows" className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {([2, 3, 4, 5, 6] as const).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-type" className="text-xs text-gray-600">
                {t('pages.items.printQrBatch.printType')}
              </Label>
              <Select
                value={printType}
                onValueChange={(v) => setPrintType(v as PrintType)}
              >
                <SelectTrigger id="print-type" className="h-8 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qr">{t('pages.items.printQrBatch.printTypeQr')}</SelectItem>
                  <SelectItem value="barcode">{t('pages.items.printQrBatch.printTypeBarcode')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="print-qr-size" className="text-xs text-gray-600">
                {t('pages.items.details.qr.sizeLabel')}
              </Label>
              <Select
                value={qrSize}
                onValueChange={(v) => setQrSize(v as 'small' | 'medium' | 'big')}
              >
                <SelectTrigger id="print-qr-size" className="h-8 w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{t('pages.items.details.qr.sizeSmall')}</SelectItem>
                  <SelectItem value="medium">{t('pages.items.details.qr.sizeMedium')}</SelectItem>
                  <SelectItem value="big">{t('pages.items.details.qr.sizeBig')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="print-include-title"
                  checked={includeTitle}
                  onCheckedChange={(c) => setIncludeTitle(c === true)}
                />
                <Label htmlFor="print-include-title" className="cursor-pointer text-xs font-normal text-gray-600">
                  {t('pages.items.details.qr.includeTitle')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="print-include-sku"
                  checked={includeSku}
                  onCheckedChange={(c) => setIncludeSku(c === true)}
                />
                <Label htmlFor="print-include-sku" className="cursor-pointer text-xs font-normal text-gray-600">
                  {t('pages.items.details.qr.includeSku')}
                </Label>
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">{t('pages.items.printQrBatch.printTip')}</span>
            <Button
              size="sm"
              disabled={isPrinting}
              onClick={() => {
                setIsPrinting(true);
                runPrint();
              }}
            >
              {isPrinting ? t('pages.items.printQrBatch.printing') : t('pages.items.printQrBatch.printButton')}
            </Button>
          </div>
        </div>
      </div>
      <div
        ref={printContentRef}
        className="print-qr-content min-h-screen p-4 print:p-4"
        style={
          {
            ['--print-cols']: columnsPerRow,
            ['--print-qr-min-cell']: `${minCellWidthPx}px`,
          } as React.CSSProperties
        }
      >
      <div className="print-qr-no-print mb-6">
        <p className="mb-2 text-xs font-medium text-gray-600">
          {t('pages.items.printQrBatch.paperSizePreview')}
        </p>
        <div
          className="print-qr-paper-cutout mx-auto overflow-hidden rounded border-2 border-dashed border-gray-400 bg-white"
          style={{
            width: previewWidth,
            height: previewHeight,
            minWidth: 200,
            minHeight: 280,
          }}
        >
          <div className="size-full overflow-hidden">
            {pageChunks[0] && (
              <div
                className="print-qr-grid grid w-full gap-4"
                style={
                  {
                    gridTemplateColumns: `repeat(${columnsPerRow}, minmax(${minCellWidthPx}px, 1fr))`,
                  } as React.CSSProperties
                }
              >
                {pageChunks[0].map((item) => (
                  <div
                    key={item.id}
                    className="print-qr-card min-w-0 rounded border border-dashed border-gray-400 p-3"
                    style={{ minWidth: minCellWidthPx }}
                  >
                    <div className="space-y-3">
                      {(includeTitle || includeSku) && (
                        <div className="space-y-1 text-center">
                          {includeTitle && (
                            <h2 className="text-lg font-semibold text-foreground print:text-black">
                              {item.name}
                            </h2>
                          )}
                          {includeSku && item.sku && (
                            <p className="text-sm text-muted-foreground print:text-gray-700">
                              {t('pages.items.form.sku')}: {item.sku}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="rounded-md border border-gray-200 bg-white px-2 pt-1.5 pb-1">
                        {printType === 'barcode' ? (
                          <img
                            src={barcodeById[item.id]}
                            alt={t('pages.items.printQrBatch.barcodeAlt')}
                            className="mx-auto block"
                            style={{ height: barcodeHeightPx, width: 'auto', maxWidth: '100%' }}
                          />
                        ) : (
                          <img
                            src={qrById[item.id]}
                            alt={t('pages.items.printQrBatch.qrAlt')}
                            className="mx-auto block"
                            width={qrWidth}
                            height={qrWidth}
                          />
                        )}
                        <img
                          src="/brand/trushelf-horizontal.svg"
                          alt="TrueShelf"
                          className="mx-auto mt-1 h-3.5 opacity-70"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {pageChunks.length > 1 && (
          <p className="mt-2 text-xs text-gray-500">
            {t('pages.items.printQrBatch.morePages', { count: pageChunks.length - 1 })}
          </p>
        )}
      </div>
      <div className="print-qr-print-only">
      {pageChunks.map((pageItems, pageIndex) => (
        <div key={pageIndex} className="print-page">
          <div
            className="print-qr-grid grid gap-4"
            style={
              {
                gridTemplateColumns: `repeat(${columnsPerRow}, minmax(${minCellWidthPx}px, 1fr))`,
              } as React.CSSProperties
            }
          >
            {pageItems.map((item) => (
              <div
                key={item.id}
                className="print-qr-card mx-auto w-full min-w-0 rounded border border-dashed border-gray-400 p-3"
                style={{ minWidth: minCellWidthPx }}
              >
                <div className="space-y-3">
                  {(includeTitle || includeSku) && (
                    <div className="space-y-1 text-center">
                      {includeTitle && (
                        <h2 className="text-lg font-semibold text-foreground print:text-black">
                          {item.name}
                        </h2>
                      )}
                      {includeSku && item.sku && (
                        <p className="text-sm text-muted-foreground print:text-gray-700">
                          {t('pages.items.form.sku')}: {item.sku}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="rounded-md border border-gray-200 bg-white px-2 pt-1.5 pb-1">
                    {printType === 'barcode' ? (
                      <img
                        src={barcodeById[item.id]}
                        alt={t('pages.items.printQrBatch.barcodeAlt')}
                        className="mx-auto block"
                        style={{ height: barcodeHeightPx, width: 'auto', maxWidth: '100%' }}
                      />
                    ) : (
                      <img
                        src={qrById[item.id]}
                        alt={t('pages.items.printQrBatch.qrAlt')}
                        className="mx-auto block"
                        width={qrWidth}
                        height={qrWidth}
                      />
                    )}
                    <img
                      src="/brand/trushelf-horizontal.svg"
                      alt="TrueShelf"
                      className="mx-auto mt-1 h-3.5 opacity-70"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      </div>
      </div>
    </div>
  );
}
